import fileTypeChecker from 'file-type-checker';

import type { FileUpload } from '@ctn/ui/types/ctn-upload';

/**
 * Convierte el buffer a base64.
 */
export const base64ToBrowser = (buffer: ArrayBuffer) =>
	window.btoa(
		[].slice
			.call(new Uint8Array(buffer))
			.map((bin) => String.fromCharCode(bin))
			.join(''),
	);

/**
 * Carga el binario de la url suministrada
 * y lo convierte a base64.
 */
export const getBase64 = async (data: Blob): Promise<string> => {
	let base64 = null;
	try {
		const buffer = await data.arrayBuffer();
		base64 = base64ToBrowser(buffer);
	} catch (e) {
		console.error(e);
		return '';
	}

	return base64;
};

export const getBase64FromUrl = async (url: string) => {
	let base64 = null;
	try {
		const response = await fetch(url);
		const buffer = await response.arrayBuffer();
		base64 = base64ToBrowser(buffer);
	} catch (e) {
		console.error(e);
	}
	return base64;
};

export const abrirVentanaDocumento = (documento: { nombre: string; contenidoBase64: string } | undefined): void => {
	if (!documento?.contenidoBase64) {
		console.error('Documento no válido');
		return;
	}

	// Crear la ventana
	const win = window.open('', documento.nombre || new Date().getTime().toString(), 'height=500, width=600');
	if (!win) {
		console.error('No se pudo abrir la ventana');
		return;
	}

	// Crear un iframe
	const iframe = document.createElement('iframe');

	// Convertir base64 a Uint8Array
	const byteArray = Uint8Array.from(atob(documento.contenidoBase64), (char) => char.charCodeAt(0));

	// Crear un Blob con el contenido PDF
	const file = new Blob([byteArray], { type: 'application/pdf' });
	const fileURL = URL.createObjectURL(file);

	// Asignar la URL al iframe
	iframe.src = fileURL;
	iframe.width = '100%';
	iframe.height = '100%';
	iframe.style.border = 'none';

	// Insertar el iframe en la ventana
	win.document.head.innerHTML = `${win.document.head.innerHTML} <title>${documento.nombre}</title>`;
	win.document.body.appendChild(iframe);
	win.document.body.style.margin = '0';
};

export const downloadFile = ({ data, name, mimeType }: { data: string; name: string; mimeType: string }) => {
	const a = document.createElement('a');
	const url = `data:${mimeType};base64,${data}`;
	a.href = url;
	a.download = name;
	a.click();
	window.URL.revokeObjectURL(url);
	a.remove();
};

/**
 * Extrae la extensión de un nombre de archivo.
 */
export const getExtension = (filename: string) => {
	const i = filename.lastIndexOf('.');

	if (i <= 0) {
		return '';
	}

	// Extraemos desde la posición i + 1 hasta el final
	return filename.substring(i + 1);
};

/**
 * @description Comprueba si el tipo de contenido real de un archivo (basado en sus Magic Numbers)
 * coincide con los tipos esperados.
 * @param file El objeto IFile que contiene el archivo crudo (rawFile).
 * @param types Array de strings de los tipos de archivo esperados (ej: ['pdf', 'jpeg']).
 * @returns Una Promise que se resuelve con un booleano (true si es válido, false si es inválido o hay error).
 */
export const checkMagicNumbersDocument = async (file: FileUpload, types: string[]): Promise<boolean> => {
	if (!file?.rawFile) return false;

	return new Promise((resolve) => {
		const reader = new FileReader();

		reader.onload = () => {
			const buffer = reader.result as ArrayBuffer;
			if (!buffer) return resolve(false);

			try {
				if (types.includes('gml')) {
					const arr = new Uint8Array(buffer).subarray(0, 4);
					let header = '';

					for (const byte of Array.from(arr)) {
						header += byte.toString(16);
					}

					const esXML = header.startsWith('3c');

					if (esXML) {
						return resolve(true);
					}

					return resolve(false);
				}

				resolve(fileTypeChecker.validateFileType(buffer, types));
			} catch (error) {
				resolve(true);
			}
		};

		reader.onerror = () => {
			console.error('Error al leer el archivo de forma cruda');
			resolve(false);
		};

		reader.readAsArrayBuffer(file.rawFile);
	});
};

export const bytesToMB = (bytes: number) => {
	if (bytes === 0) return 0;

	const megabytes = bytes / (1024 * 1024);

	// Retorna el número redondeado a 2 decimales
	return Math.round(megabytes * 100) / 100;
};

export const convertBase64ToUrl = (base64?: string) => {
	if (!base64) return undefined;

	try {
		const byteCharacters = atob(base64);
		const byteNumbers = new Array(byteCharacters.length);
		for (let i = 0; i < byteCharacters.length; i++) {
			byteNumbers[i] = byteCharacters.charCodeAt(i);
		}
		const byteArray = new Uint8Array(byteNumbers);
		const blob = new Blob([byteArray], { type: 'application/pdf' });

		return URL.createObjectURL(blob);
	} catch (error) {
		console.error('Error al convertir PDF', error);
		return undefined;
	}
};
